import React, { useEffect, useState, useMemo } from 'react';
import ApexChart from 'react-apexcharts';
import { Box, ButtonGroup, Button, CircularProgress, Typography, Alert, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { getCachedChart } from '../services/chartCacheService';
import { getPublicChart } from '../services/publicChartService';

const RANGES = [
  { label: '24H', value: '24h' },
  { label: '1D', value: '1d' },
  { label: '5D', value: '5d' },
  { label: '1M', value: '1m' },
  { label: '6M', value: '6m' },
  { label: '1Y', value: '1y' },
  { label: '5Y', value: '5y' },
  { label: 'ALL', value: 'all' },
];

const PROVIDERS = [
  'CoinGecko',
  'CryptoCompare',
  'Binance',
  'Coinbase',
  'Kraken',
  'KuCoin',
];

// Price formatting function
function formatPrice(val) {
  if (val === null || val === undefined) return 'N/A';
  if (Math.abs(val) >= 100) return '$' + Math.round(val).toLocaleString();
  if (Math.abs(val) >= 1) return '$' + val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return '$' + val.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
}
// Volume formatting function
function formatVolume(val) {
  if (val === null || val === undefined) return 'N/A';
  if (val >= 1e9) return (val / 1e9).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + 'B';
  if (val >= 1e6) return (val / 1e6).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + 'M';
  if (val >= 1e3) return (val / 1e3).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + 'K';
  return val.toLocaleString();
}

// Relative time formatting function
function getRelativeTime(date) {
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) {
    return `${diffInSeconds} second${diffInSeconds !== 1 ? 's' : ''} ago`;
  }
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
  }
  
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
}

function CoinChart({ symbol, avgBuyPrice, estimatedMax, exitStepLines = [] }) {
  // console.log('CoinChart avgBuyPrice:', avgBuyPrice);
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const isLoggedIn = !!currentUser;
  const [range, setRange] = useState('1y');
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        // Use appropriate chart service based on authentication status
        const data = isLoggedIn 
          ? await getCachedChart(symbol, range)
          : await getPublicChart(symbol, range);
        if (!cancelled) setChartData(data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [symbol, range, isLoggedIn]);

  const priceSeries = useMemo(() => {
    if (!chartData?.data) return [];
    return [{
      name: t('common.price'),
      data: chartData.data.map(d => [d.timestamp, d.close])
    }];
  }, [chartData]);

  // Determine line color
  const lineColor = useMemo(() => {
    if (!chartData?.data || chartData.data.length < 2) return '#008000';
    const first = chartData.data[0].close;
    const last = chartData.data[chartData.data.length - 1].close;
    return last >= first ? '#008000' : '#d32f2f';
  }, [chartData]);

  const options = useMemo(() => {
    const maxVolume = chartData?.data ? Math.max(...chartData.data.map(d => d.volume || 0)) : 0;
    // Add min and max price annotation lines
    const yaxisAnnotations = [];
    if (chartData?.data && chartData.data.length > 1) {
      const minPrice = Math.min(...chartData.data.map(d => d.close));
      const maxPrice = Math.max(...chartData.data.map(d => d.close));
      yaxisAnnotations.push({
        y: minPrice,
        borderColor: '#1976d2',
        strokeDashArray: 4,
        label: {
          borderColor: '#1976d2',
          style: { color: '#fff', background: '#1976d2', fontSize: '11px', fontWeight: 'normal', lineHeight: '1.2' },
          text: `Min: ${formatPrice(minPrice)}`,
          position: 'left',
        }
      });
      yaxisAnnotations.push({
        y: maxPrice,
        borderColor: '#1976d2',
        strokeDashArray: 4,
        label: {
          borderColor: '#1976d2',
          style: { color: '#fff', background: '#1976d2', fontSize: '11px', fontWeight: 'normal', lineHeight: '1.2' },
          text: `Max: ${formatPrice(maxPrice)}`,
          position: 'left',
        }
      });
    }
    if (avgBuyPrice && avgBuyPrice > 0) {
      yaxisAnnotations.push({
        y: avgBuyPrice,
        borderColor: '#ff5722',
        borderWidth: 2,
        strokeDashArray: 0,
        label: {
          borderColor: '#ff5722',
          style: { color: '#fff', background: '#ff5722', fontSize: '11px', fontWeight: 'normal', lineHeight: '1.2' },
          text: `${t('common.buyPrice')}: ${formatPrice(avgBuyPrice)}`,
          position: 'left',
        }
      });
    }
    // Add estimated max line if provided
    if (estimatedMax && estimatedMax > 0) {
      yaxisAnnotations.push({
        y: estimatedMax,
        borderColor: '#ff9800',
        borderWidth: 2,
        strokeDashArray: 5,
        label: {
          borderColor: '#ff9800',
          style: { color: '#fff', background: '#ff9800', fontSize: '11px', fontWeight: 'normal', lineHeight: '1.2' },
          text: `${t('exitStrategy.estimatedMax')}: ${formatPrice(estimatedMax)}`,
          position: 'left',
        }
      });
    }
    // Add exit step lines
    if (exitStepLines && Array.isArray(exitStepLines)) {
      exitStepLines.forEach((step, idx) => {
        if (step.price > 0) {
          yaxisAnnotations.push({
            y: step.price,
            borderColor: '#8e24aa',
            borderWidth: 2,
            strokeDashArray: 0,
            label: {
              borderColor: '#8e24aa',
              style: { color: '#fff', background: '#8e24aa', fontSize: '11px', fontWeight: 'normal', lineHeight: '1.2' },
              text: `${t('exitStrategy.exit')} ${step.index + 1}: ${formatPrice(step.price)}`,
              position: 'left',
            }
          });
        }
      });
    }
    // Calculate dynamic y-axis min and max
    let minPrice = chartData?.data && chartData.data.length > 0 ? Math.min(...chartData.data.map(d => d.close)) : undefined;
    let maxPrice = chartData?.data && chartData.data.length > 0 ? Math.max(...chartData.data.map(d => d.close)) : undefined;
    let dynamicMin = minPrice;
    let dynamicMax = maxPrice;
    if (avgBuyPrice && avgBuyPrice > 0) {
      dynamicMin = Math.min(dynamicMin, avgBuyPrice);
      dynamicMax = Math.max(dynamicMax, avgBuyPrice);
    }
    if (estimatedMax && estimatedMax > 0) {
      dynamicMax = Math.max(dynamicMax, estimatedMax);
    }
    if (dynamicMin !== undefined) {
      dynamicMin = dynamicMin * 0.98; // add a 2% margin below
    }
    if (dynamicMax !== undefined) {
      dynamicMax = dynamicMax * 1.02; // add a 2% margin above
    }
    const opts = {
      chart: {
        id: 'coin-chart',
        type: 'line',
        height: 350,
        toolbar: { show: false }, // Ensure ApexCharts toolbar is hidden
        zoom: { enabled: false },
        selection: { enabled: false }, // Hide range selection
        brush: { enabled: false },     // Hide brush selection
      },
      stroke: {
        width: [3, 0],
        curve: 'straight',
        colors: [lineColor],
      },
      colors: [lineColor, '#90caf9'],
      xaxis: {
        type: 'datetime',
        labels: { show: true },
        range: undefined, // Ensure no range selector
      },
      yaxis: [
        {
          seriesName: t('common.price'),
          labels: { formatter: formatPrice, style: { fontWeight: 'bold' } },
          title: { text: `${t('common.price')} (USD)` },
          min: dynamicMin,
          max: dynamicMax,
        },
        {
          show: false,
        }
      ],
      tooltip: {
        shared: true,
        x: { format: 'dd MMM yyyy HH:mm' },
        y: [
          {
            formatter: val => `$${val?.toFixed(4)}`
          }
        ],
        custom: function({series, seriesIndex, dataPointIndex, w}) {
          const price = w.globals.initialSeries[0].data[dataPointIndex][1];
          let html = `<div style="background: #f3f3f3; padding: 6px 10px; border-radius: 6px;">`;
          html += `<div>${t('common.price')}: <span style="font-weight:bold">${formatPrice(price)}</span></div>`;
          if (avgBuyPrice) {
            html += `<div>${t('common.buyPrice')}: ${formatPrice(avgBuyPrice)}</div>`;
          }
          if (exitStepLines && exitStepLines.length > 0) {
            html += `<div style='margin-top:4px;'><b>${t('exitStrategy.exitSteps')}:</b></div>`;
            exitStepLines.forEach((step, idx) => {
              html += `<div>${t('exitStrategy.exit')} ${step.index + 1}: <span style='font-weight:bold;'>${formatPrice(step.price)}</span></div>`;
            });
          }
          html += `</div>`;
          return html;
        }
      },
      legend: { show: false },
      grid: { show: true, borderColor: '#eee' },
      plotOptions: {
        bar: {
          columnWidth: '60%',
          borderRadius: 2,
        }
      },
      annotations: yaxisAnnotations.length > 0 ? { yaxis: yaxisAnnotations } : {},
    };
    return opts;
  }, [lineColor, chartData, avgBuyPrice, estimatedMax, exitStepLines]);

  return (
    <Box>
      {loading && <Box display="flex" justifyContent="center" p={0.5}><CircularProgress /></Box>}
      {error && <Alert severity="error">{error}</Alert>}

      {chartData && !loading && !error && (
        <ApexChart
          key={symbol + '-' + avgBuyPrice + '-' + estimatedMax + '-' + JSON.stringify(exitStepLines)}
          type="line"
          height={160}
          series={[
            ...priceSeries
          ]}
          options={{
            ...options,
            yaxis: options.yaxis,
            stroke: options.stroke,
            colors: options.colors,
            xaxis: options.xaxis,
            tooltip: options.tooltip,
            grid: options.grid,
          }}
        />
      )}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, gap: 0.2, flexWrap: 'nowrap', pb: 0.5 }}>
        {RANGES.map(r => (
          <Button
            key={r.value}
            onClick={() => setRange(r.value)}
            variant="text"
            sx={{
              borderRadius: 1,
              minWidth: 32,
              fontWeight: 400,
              fontSize: '0.85rem',
              px: 0.8,
              py: 0.2,
              textTransform: 'none',
              bgcolor: range === r.value ? '#e6f0fa' : 'transparent',
              color: range === r.value ? '#1976d2' : '#222',
              boxShadow: 'none',
              border: 'none',
              transition: 'background 0.15s',
              height: 28,
              lineHeight: 1.1,
              '&:hover': {
                bgcolor: range === r.value ? '#d0e6fa' : '#f5f5f5',
                color: range === r.value ? '#1976d2' : '#222',
              },
            }}
          >
            {r.label}
          </Button>
        ))}
      </Box>
    </Box>
  );
}

export default CoinChart; 